import React, { useState } from 'react';
import { cashflowApi } from '../services/api';
import '../styles/Modal.css';

function GenerateCashflowModal({ entity, onClose, onComplete }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [jobStatus, setJobStatus] = useState('');

  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    // Validate date order
    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before end date');
      return;
    }

    setGenerating(true);
    setError(null);
    setResult(null);
    setJobStatus('Queuing cashflow job...');

    try {
      const queued = await cashflowApi.generateCashflowAsync(entity.name, startDate, endDate);
      const jobId = queued.job_id;
      if (!jobId) {
        throw new Error('Failed to queue cashflow generation job');
      }

      // Poll backend job status to avoid long-lived request timeouts.
      let finalResult = null;
      const maxAttempts = 600; // 30 mins at 3s interval
      for (let i = 0; i < maxAttempts; i += 1) {
        const statusResp = await cashflowApi.getCashflowGenerateStatus(jobId);
        const status = statusResp?.status || 'queued';
        setJobStatus(`Job ${jobId.slice(0, 8)}: ${status}`);

        if (status === 'success' && statusResp?.result) {
          finalResult = statusResp.result;
          break;
        }
        if (status === 'failed') {
          throw new Error(statusResp?.error?.message || 'Cashflow generation failed');
        }

        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }

      if (!finalResult) {
        throw new Error('Cashflow generation timed out while waiting for job completion');
      }

      setResult(finalResult);

      // Auto-close and trigger refresh after 2 seconds
      setTimeout(() => {
        onComplete(finalResult);
      }, 2000);
    } catch (err) {
      console.error('Error generating cashflow:', err);
      setError(err.message || 'Failed to generate cashflow');
    } finally {
      setGenerating(false);
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return num.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const calculatePriorPeriod = () => {
    if (!startDate || !endDate) return null;

    const start = new Date(startDate);

    // Period 2 start: Same month/day as Period 1 start, but 1 year earlier
    const priorStart = new Date(start);
    priorStart.setFullYear(priorStart.getFullYear() - 1);

    // Period 2 end: Day before Period 1 start (which is the end of last FY)
    // For example, if Period 1 starts on April 1, 2025, then Period 2 ends on March 31, 2025
    const priorEnd = new Date(start);
    priorEnd.setDate(priorEnd.getDate() - 1);

    return {
      start: priorStart.toISOString().split('T')[0],
      end: priorEnd.toISOString().split('T')[0],
    };
  };

  const priorPeriod = calculatePriorPeriod();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h2>Generate Cashflow Statement</h2>
          <button className="modal-close" onClick={onClose} disabled={generating}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {!result && (
            <>
              <div className="alert alert-info" style={{ fontSize: '14px' }}>
                <strong>How it works:</strong>
                <ul style={{ marginTop: '8px', marginLeft: '20px', marginBottom: 0 }}>
                  <li>System pulls Trial Balance for your selected period (Period 1) AND for the last complete fiscal year (Period 2)</li>
                  <li>Period 2 is automatically calculated as the last FY ending before Period 1 starts</li>
                  <li>System pulls General Ledger transactions for your selected period</li>
                  <li>All workings are generated with formula-driven calculations</li>
                  <li>Excel output includes 9 tabs: TB, GL, Period Movements, 5 cashflow workings (W1-W4, with W3 split into Current and Non-current), and Finalyzer Output</li>
                </ul>
              </div>

              <div className="form-group">
                <label>Start Date:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={generating}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>End Date:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={generating}
                  className="form-control"
                />
              </div>

              {priorPeriod && (
                <div className="alert alert-success" style={{ fontSize: '14px' }}>
                  <strong>Current Period:</strong> {startDate} to {endDate}
                  <br />
                  <strong>Prior Period (auto):</strong> {priorPeriod.start} to {priorPeriod.end}
                </div>
              )}

              {error && (
                <div className="alert alert-danger">
                  <strong>Error:</strong> {error}
                </div>
              )}

              {generating && (
                <div className="alert alert-info" style={{ marginTop: '12px', fontSize: '14px' }}>
                  Cashflow generation is in progress. This may take up to 20 minutes.
                  <br />
                  <small>{jobStatus}</small>
                </div>
              )}
            </>
          )}

          {result && (
            <div className="alert alert-success">
              <strong>✓ Cashflow Generated Successfully!</strong>
              <ul style={{ marginTop: '12px', marginLeft: '20px' }}>
                <li><strong>Entity:</strong> {result.metadata.entity_name}</li>
                <li><strong>Period:</strong> {result.metadata.start_date} to {result.metadata.end_date}</li>
                <li><strong>TB Current Records:</strong> {formatNumber(result.metadata.tb_current_records)}</li>
                <li><strong>TB Prior Records:</strong> {formatNumber(result.metadata.tb_prior_records)}</li>
                <li><strong>GL Records:</strong> {formatNumber(result.metadata.gl_records)}</li>
                <li><strong>Period Movements:</strong> {formatNumber(result.metadata.period_movements_count)} accounts</li>
                <li><strong>Timestamp:</strong> {result.metadata.timestamp}</li>
              </ul>
              <p style={{ marginTop: '12px', marginBottom: 0 }}>
                <small>Closing in 2 seconds...</small>
              </p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {!result && (
            <>
              <button
                onClick={handleGenerate}
                disabled={generating || !startDate || !endDate}
                className="btn btn-primary"
              >
                {generating ? 'Generating...' : 'Generate Cashflow'}
              </button>
              <button onClick={onClose} disabled={generating} className="btn btn-secondary">
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default GenerateCashflowModal;
